import { useQuery } from '@tanstack/react-query';
import placesService from '~/services/placesService/places.service';
import { useState, useEffect } from 'react';
import { Period } from '~/constants/period.constants';
import { GooglePlace } from '@bitecritic-api/src/places/places.controller';
import { GooglePlaceDetails } from '@bitecritic-api/src/places/places.service';

interface ApiError {
  response?: {
    status: number;
    data?: {
      message?: string;
    };
  };
}

const PLACES_QUERY_KEY = 'places';
const LAST_SELECTED_PLACE_KEY = 'bitecritic_last_selected_place';

interface UseNearbyPlacesParams {
  latitude?: number;
  longitude?: number;
  googlePlaceId?: string;
  radiusInKm?: number;
  page?: number;
  limit?: number;
  period?: Period;
  username?: string;
  sort?:
    | 'name-asc'
    | 'name-desc'
    | 'rating-desc'
    | 'rating-asc'
    | 'reviewCount-desc'
    | 'reviewCount-asc';
}

export const useNearbyPlaces = (
  params: UseNearbyPlacesParams,
  isRouterReady: boolean,
) => {
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [placeDetails, setPlaceDetails] = useState<GooglePlaceDetails | null>(null);

  // Determine if we have valid search criteria
  // For username views, we always consider it valid to fetch data
  const hasValidCriteria = Boolean(
    params.username || (params.latitude && params.longitude) || params.googlePlaceId
  );

  // Clean up params to ensure we don't send undefined values
  const finalParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key as keyof UseNearbyPlacesParams] = value;
    }
    return acc;
  }, {} as UseNearbyPlacesParams);

  const queryResult = useQuery({
    queryKey: [
      PLACES_QUERY_KEY,
      'nearby',
      finalParams.latitude,
      finalParams.longitude,
      finalParams.googlePlaceId,
      finalParams.radiusInKm,
      finalParams.page,
      finalParams.period,
      finalParams.sort,
      finalParams.username,
    ],
    queryFn: async () => {
      try {
        setErrorMessage(null);
        const result = await placesService.getNearbyPlaces(finalParams);
        setIsRateLimited(false);
        
        // If we have results and a googlePlaceId was used, store the place details
        if (result && params.googlePlaceId && result.items && result.items.length > 0) {
          // Find the selected place in the results
          const selectedPlace = result.googlePlaceDetails;
          
          if (selectedPlace) {
            setPlaceDetails(selectedPlace);
            
            // Save to localStorage for persistence
            try {
              
              localStorage.setItem(
                LAST_SELECTED_PLACE_KEY,
                JSON.stringify({
                  id: selectedPlace.googlePlaceId,
                  name: selectedPlace.name,
                  latitude: selectedPlace.latitude,
                  longitude: selectedPlace.longitude,
                })
              );
            } catch (e) {
              console.error('Failed to save place to localStorage:', e);
            }
          }
        }
        
        return result;
      } catch (error) {
        const apiError = error as ApiError;
        if (apiError?.response?.status === 429) {
          setIsRateLimited(true);
          setErrorMessage('Rate limit exceeded. Please try again later.');
          return { items: [], total: 0 };
        }

        // Handle other API errors
        const message =
          apiError?.response?.data?.message || 'Failed to fetch places';
        setErrorMessage(message);
        throw error;
      }
    },
    retry: (failureCount, error: ApiError) => {
      // Don't retry on rate limit errors
      if (error?.response?.status === 429) return false;
      // Don't retry on 400 errors (bad request)
      if (error?.response?.status === 400) return false;
      return failureCount < 3;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: isRouterReady && hasValidCriteria,
  });

  return {
    ...queryResult,
    isRateLimited,
    errorMessage,
    hasValidCriteria,
    placeDetails,
  };
};

// Hook to get the last selected place from localStorage
export const useLastSelectedPlace = () => {
  const [lastSelectedPlace, setLastSelectedPlace] = useState<GooglePlace | null>(null);
  
  useEffect(() => {
    if (typeof window === 'undefined') return; // Skip on server-side
    
    try {
      const savedPlace = localStorage.getItem(LAST_SELECTED_PLACE_KEY);
      if (savedPlace) {
        setLastSelectedPlace(JSON.parse(savedPlace));
      }
    } catch (e) {
      console.error('Failed to load place from localStorage:', e);
    }
  }, []);
  
  return lastSelectedPlace;
};

// Hook for place autocomplete with caching
export const useAutocomplete = () => {
  const [results, setResults] = useState<GooglePlace[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const searchQuery = useQuery({
    queryKey: ['places', 'autocomplete'],
    queryFn: async () => {
      return results;
    },
    enabled: false, // Don't run query on mount
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
  
  const refetch = async (query: string, language?: string, countryCode?: string) => {
    if (!query || query.length < 2) {
      setResults([]);
      return [];
    }
    
    setIsLoading(true);
    try {
      const results = await searchPlaces(query, language, countryCode);
      setResults(results);
      setError(null);
      return results;
    } catch (error) {
      console.error('Autocomplete search failed:', error);
      setError(error as Error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    ...searchQuery,
    data: results,
    isLoading,
    error,
    refetch,
  };
};

// Function to search places with autocomplete (to be used with useQuery's refetch)
export const searchPlaces = async (
  query: string,
  language?: string,
  countryCode?: string
) => {
  if (!query || query.length < 2) return [];
  
  try {
    return await placesService.autocomplete(query, language, countryCode);
  } catch (error) {
    console.error('Autocomplete search failed:', error);
    return [];
  }
};
